/**
 * WhatsApp template compose (single recipient). WhatsApp only sends via approved
 * templates, so this is the only compose path. Two-column layout: content on the
 * left, a live preview on the right. Validates all required inputs, then sends and
 * shows a confirmation modal.
 */

import { useState } from 'react';
import { IconSend, IconCircleCheck } from '@tabler/icons-react';
import { useHttpClient } from '@actschurch/ccms-sdk';
import { Stack, Group, Text, Title, TextInput, Button, Switch, Alert, Divider, Modal, Grid } from '../../../../../components/ui';
import { Card } from '../../../../../components/ui/data-display';
import { getApiUrl } from '../../../../../config/ccms';
import { toast } from '../../../../../components/ui/providers/AlertProvider';
import { WhatsAppTemplateComposer, type ComposerOutput } from '../../_shared/WhatsAppTemplateComposer';
import { WhatsAppMessagePreview } from '../../_shared/WhatsAppMessagePreview';
import { buildSendComponents } from '../../_shared/whatsappTemplate';

export function Dialog360TemplateCompose() {
  const httpClient = useHttpClient();
  const [recipient, setRecipient] = useState('');
  const [includeOptedOut, setIncludeOptedOut] = useState(false);
  const [out, setOut] = useState<ComposerOutput | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<{ recipient: string; templateName: string } | null>(null);
  const [formKey, setFormKey] = useState(0);

  const reset = () => {
    setSent(null);
    setRecipient('');
    setOut(null);
    setError(null);
    setMissing([]);
    setFormKey((k) => k + 1); // remount composer to clear template/media/params
  };

  const [missing, setMissing] = useState<string[]>([]);
  const recipientOk = recipient.trim().length > 0;

  const handleSend = async () => {
    setError(null);
    // Collect every missing required field so the user sees them all at once.
    const miss: string[] = [];
    if (!recipientOk) miss.push('Recipient phone number');
    if (!out?.template || !out.structure) miss.push('Select an approved template');
    else miss.push(...out.errors);
    setMissing(miss);
    if (miss.length > 0 || !out?.structure) return;

    const components = buildSendComponents(out.structure, {
      mediaUrl: out.mediaUrl,
      bodyValues: out.bodyValues,
      buttonUrlParam: out.buttonUrlParam,
    });

    setSending(true);
    try {
      await httpClient.post(getApiUrl('communications', '/api/dialog360/send/template'), {
        recipient: recipient.trim(),
        templateName: out.templateName,
        languageCode: out.templateLanguage || 'en',
        components,
        includeOptedOut,
      });
      toast.success('WhatsApp template sent');
      setSent({ recipient: recipient.trim(), templateName: out.templateName });
    } catch (err: any) {
      const msg = err?.message || 'Failed to send';
      setError(msg);
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Grid gutter="xl">
        {/* LEFT — content (70%) */}
        <Grid.Col span={{ base: 12, lg: 8 }}>
        <Stack gap="md">
          <Card withBorder padding="md">
            <TextInput
              label="Recipient"
              placeholder="+27821234567"
              value={recipient}
              onChange={(e) => setRecipient(e.currentTarget.value)}
              required
            />
          </Card>

          <WhatsAppTemplateComposer key={formKey} mode="single" onChange={setOut} />

          {missing.length > 0 && (
            <Alert color="red" title="Please complete the following">
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {missing.map((m, i) => <li key={i}>{m}</li>)}
              </ul>
            </Alert>
          )}
          {error && <Alert color="red">{error}</Alert>}

          <Card withBorder padding="md">
            <Stack gap="sm">
              <Switch
                checked={includeOptedOut}
                onChange={(e) => setIncludeOptedOut(e.currentTarget.checked)}
                label="Send even to opted-out / blocked contacts"
                description="Bypasses our internal opt-out suppression. Note: WhatsApp/Meta still cannot deliver to users who have blocked the business number."
              />
              <Divider />
              <Group justify="flex-end">
                <Button onClick={handleSend} loading={sending} leftSection={<IconSend size={16} />} gradientVariant="primary">
                  Send message
                </Button>
              </Group>
            </Stack>
          </Card>
        </Stack>
        </Grid.Col>

        {/* RIGHT — live preview (30%) */}
        <Grid.Col span={{ base: 12, lg: 4 }}>
        <Stack gap="xs">
          <Text size="sm" fw={500}>Preview</Text>
          <div style={{ position: 'sticky', top: 16 }}>
            <WhatsAppMessagePreview template={out?.template} mediaUrl={out?.mediaUrl} bodyValues={out?.bodyValues} />
          </div>
        </Stack>
        </Grid.Col>
      </Grid>

      {/* Confirmation modal — both actions reset the form */}
      <Modal opened={sent !== null} onClose={reset} title="Message sent" centered>
        <Stack gap="md" align="center">
          <IconCircleCheck size={56} color="var(--mantine-color-green-6)" />
          <Title order={4}>Sent successfully</Title>
          <Text size="sm" c="dimmed" ta="center">
            Template <b>{sent?.templateName}</b> was sent to <b>{sent?.recipient}</b>. Track delivery on the Outbox.
          </Text>
          <Group>
            <Button variant="light" onClick={reset}>Send another</Button>
            <Button onClick={reset}>Done</Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
